import { STATION_CHARGE_FILTERS, STATION_COLORS, type StationChargeFilters } from '../constants';
import {
  STATION_ICONS,
  type StationIconConfig,
  type StationRangeKey,
} from '../constants/stationIcons';

/**
 * Threshold values for station charge ranges
 * IMPORTANT: These values build MapLayersService's STATION_SYMBOL_LAYOUT
 * If you change these, update the Mapbox step expression accordingly
 */
export const STATION_CHARGE_THRESHOLDS = {
  RANGE_50_PLUS: 50,
  RANGE_30_50: 30,
  RANGE_25_30: 25,
  RANGE_20_25: 20,
  RANGE_15_20: 15,
  RANGE_10_15: 10,
} as const;

/**
 * Half-open buckets behind both the Mapbox station filter and the crown check.
 * `min` is inclusive, `max` is exclusive. The bottom bucket has no floor, so
 * charges under 1 stay in "< 1 to 10".
 */
export const STATION_CHARGE_RANGE_BOUNDS: ReadonlyArray<{
  label: StationChargeFilters;
  min?: number;
  max?: number;
}> = [
  { label: STATION_CHARGE_FILTERS[0], max: STATION_CHARGE_THRESHOLDS.RANGE_10_15 },
  {
    label: STATION_CHARGE_FILTERS[1],
    min: STATION_CHARGE_THRESHOLDS.RANGE_10_15,
    max: STATION_CHARGE_THRESHOLDS.RANGE_15_20,
  },
  {
    label: STATION_CHARGE_FILTERS[2],
    min: STATION_CHARGE_THRESHOLDS.RANGE_15_20,
    max: STATION_CHARGE_THRESHOLDS.RANGE_20_25,
  },
  {
    label: STATION_CHARGE_FILTERS[3],
    min: STATION_CHARGE_THRESHOLDS.RANGE_20_25,
    max: STATION_CHARGE_THRESHOLDS.RANGE_25_30,
  },
  {
    label: STATION_CHARGE_FILTERS[4],
    min: STATION_CHARGE_THRESHOLDS.RANGE_25_30,
    max: STATION_CHARGE_THRESHOLDS.RANGE_30_50,
  },
  {
    label: STATION_CHARGE_FILTERS[5],
    min: STATION_CHARGE_THRESHOLDS.RANGE_30_50,
    max: STATION_CHARGE_THRESHOLDS.RANGE_50_PLUS,
  },
  { label: STATION_CHARGE_FILTERS[6], min: STATION_CHARGE_THRESHOLDS.RANGE_50_PLUS },
];

/** True when `charges` falls in the bucket named by `label`. */
export function chargeFallsInStationRange(charges: number, label: StationChargeFilters): boolean {
  const range = STATION_CHARGE_RANGE_BOUNDS.find((bound) => bound.label === label);
  if (!range) return false;
  if (range.min != null && charges < range.min) return false;
  if (range.max != null && charges >= range.max) return false;
  return true;
}

export interface StationChargeInfo {
  color: string;
  id: string;
  rangeKey: StationRangeKey;
  label: string;
  icons: StationIconConfig;
}

/**
 * Get the station marker info based on charges_per_station_per_day
 * Matches MapLayersService's STATION_SYMBOL_LAYOUT icon steps
 * Returns color, id, rangeKey, and icons (marker for map, icon for UI)
 */
export function getStationByCharges(chargesPerDay: number | undefined): StationChargeInfo {
  const charges = chargesPerDay ?? 0;

  let stationColor: (typeof STATION_COLORS)[StationRangeKey];

  if (charges >= STATION_CHARGE_THRESHOLDS.RANGE_50_PLUS) {
    stationColor = STATION_COLORS.RANGE_50_PLUS;
  } else if (charges >= STATION_CHARGE_THRESHOLDS.RANGE_30_50) {
    stationColor = STATION_COLORS.RANGE_30_50;
  } else if (charges >= STATION_CHARGE_THRESHOLDS.RANGE_25_30) {
    stationColor = STATION_COLORS.RANGE_25_30;
  } else if (charges >= STATION_CHARGE_THRESHOLDS.RANGE_20_25) {
    stationColor = STATION_COLORS.RANGE_20_25;
  } else if (charges >= STATION_CHARGE_THRESHOLDS.RANGE_15_20) {
    stationColor = STATION_COLORS.RANGE_15_20;
  } else if (charges >= STATION_CHARGE_THRESHOLDS.RANGE_10_15) {
    stationColor = STATION_COLORS.RANGE_10_15;
  } else {
    stationColor = STATION_COLORS.RANGE_1_10;
  }

  return {
    ...stationColor,
    icons: STATION_ICONS[stationColor.rangeKey],
  };
}

/** Mapbox `step` stops. Charges under 1 share the "< 1 to 10" icon. Stops ascend. */
export function stationChargeIconStep(iconId: (rangeKey: StationRangeKey) => string): unknown[] {
  return [
    'step',
    ['coalesce', ['get', 'charges_per_station_per_day'], 0],
    iconId('RANGE_1_10'),
    STATION_CHARGE_THRESHOLDS.RANGE_10_15,
    iconId('RANGE_10_15'),
    STATION_CHARGE_THRESHOLDS.RANGE_15_20,
    iconId('RANGE_15_20'),
    STATION_CHARGE_THRESHOLDS.RANGE_20_25,
    iconId('RANGE_20_25'),
    STATION_CHARGE_THRESHOLDS.RANGE_25_30,
    iconId('RANGE_25_30'),
    STATION_CHARGE_THRESHOLDS.RANGE_30_50,
    iconId('RANGE_30_50'),
    STATION_CHARGE_THRESHOLDS.RANGE_50_PLUS,
    iconId('RANGE_50_PLUS'),
  ];
}
